(function () {
  'use strict';

  const UG_MARATHON_MOBILE_MQ = '(max-width: 1023.98px)';
  const UG_MARATHON_GA_PRODUCT_DUR = 0.9;

  let ugMarathonGsapRegistered = false;
  let ugMarathonCachedMobile = null;

  function ugMarathonSyncMobileState() {
    ugMarathonCachedMobile =
      typeof window.matchMedia === 'function' &&
      window.matchMedia(UG_MARATHON_MOBILE_MQ).matches;
  }

  function ugMarathonIsMobile() {
    if (ugMarathonCachedMobile === null) ugMarathonSyncMobileState();
    return ugMarathonCachedMobile;
  }

  function ugMarathonPrefersReducedMotion() {
    return (
      typeof window.matchMedia === 'function' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches
    );
  }

  function ugMarathonEnsureGsapRegistered() {
    if (ugMarathonGsapRegistered) return true;
    const gsap = window.gsap;
    if (!gsap) return false;
    if (window.ScrollTrigger) {
      gsap.registerPlugin(window.ScrollTrigger);
    }
    ugMarathonGsapRegistered = true;
    return true;
  }

  function ugMarathonDebounce(fn, wait) {
    let timer = null;
    return function debounced() {
      if (timer) window.clearTimeout(timer);
      timer = window.setTimeout(() => {
        timer = null;
        fn();
      }, wait);
    };
  }

  function ugMarathonRafThrottle(fn) {
    let ticking = false;
    return function () {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => {
        ticking = false;
        fn();
      });
    };
  }

  class UgMarathonSection extends HTMLElement {
    connectedCallback() {
      if (this.dataset.ugMarathonReady === '1') return;
      this.dataset.ugMarathonReady = '1';
      this.boot();
    }

    disconnectedCallback() {
      if (this._ugMarathonBootTimer) {
        window.clearTimeout(this._ugMarathonBootTimer);
        this._ugMarathonBootTimer = null;
      }
      this.killKvBanner();
      this.killGiveaway();
      this.killSuccessModal();
      this.killStory();
      this.killProducts();
      this.killSceneCarousel();
      this.killPhotoGallery();
      this.killEventFeatures();
      this.killReveal();
      this.killStickyBar();
      this.killStickyNav();
      this.dataset.ugMarathonReady = '0';
    }

    boot() {
      const start = () => {
        this._ugMarathonBootTimer = null;
        this.init();
      };

      if (window.gsap) {
        start();
        return;
      }

      let tries = 0;
      const tick = () => {
        tries += 1;
        if (window.gsap || tries >= 40) {
          start();
          return;
        }
        this._ugMarathonBootTimer = window.setTimeout(tick, 50);
      };
      this._ugMarathonBootTimer = window.setTimeout(tick, 50);
    }

    safeInit(name, fn) {
      try {
        fn.call(this);
      } catch (err) {
        console.error('[ug-marathon] ' + name + ' failed:', err);
      }
    }

    init() {
      ugMarathonSyncMobileState();
      ugMarathonEnsureGsapRegistered();
      this.safeInit('initKvBanner', this.initKvBanner);
      this.safeInit('initStickyNav', this.initStickyNav);
      this.safeInit('bindGiveaway', this.bindGiveaway);
      this.safeInit('bindSuccessModal', this.bindSuccessModal);
      this.safeInit('bindStory', this.bindStory);
      this.safeInit('bindProducts', this.bindProducts);
      this.safeInit('bindSceneCarousel', this.bindSceneCarousel);
      this.safeInit('bindPhotoGallery', this.bindPhotoGallery);
      this.safeInit('bindEventFeatures', this.bindEventFeatures);
      this.safeInit('bindReveal', this.bindReveal);
      this.safeInit('initStickyBar', this.initStickyBar);
    }

    killKvBanner() {
      if (this._ugMarathonKvTls && this._ugMarathonKvTls.length) {
        this._ugMarathonKvTls.forEach((tl) => {
          if (tl) tl.kill();
        });
        this._ugMarathonKvTls = [];
      }

      this.querySelectorAll('[data-ug-marathon-kv]').forEach((root) => {
        root.classList.add('is-kv-ready');
        root.dataset.ugMarathonKvBound = '0';
        root.querySelectorAll('[data-ug-marathon-kv-logo], [data-ug-marathon-kv-copy]').forEach((el) => {
          if (window.gsap) {
            window.gsap.set(el, { clearProps: 'opacity,visibility,y,transform' });
          } else {
            el.style.opacity = '';
            el.style.visibility = '';
            el.style.transform = '';
          }
        });
      });
    }

    initKvBanner() {
      const roots = this.querySelectorAll('[data-ug-marathon-kv]');
      if (!roots.length) return;

      const markReady = (root) => {
        root.classList.add('is-kv-ready');
      };

      if (ugMarathonPrefersReducedMotion() || !ugMarathonEnsureGsapRegistered()) {
        roots.forEach(markReady);
        return;
      }

      const gsap = window.gsap;
      if (!this._ugMarathonKvTls) this._ugMarathonKvTls = [];

      roots.forEach((root) => {
        if (root.dataset.ugMarathonKvBound === '1') return;
        root.dataset.ugMarathonKvBound = '1';

        const logo = root.querySelector('[data-ug-marathon-kv-logo]');
        const copy = root.querySelector('[data-ug-marathon-kv-copy]');
        if (!logo && !copy) {
          markReady(root);
          return;
        }

        // Fallback: never leave copy permanently hidden if tween fails
        const readyFallback = window.setTimeout(() => markReady(root), 2200);

        let fromY = -28;
        try {
          const rToken = getComputedStyle(this).getPropertyValue('--ug-marathon-kv-r').trim();
          const rNum = parseFloat(rToken);
          if (Number.isFinite(rNum) && rNum > 0 && rToken.indexOf('calc') === -1) {
            fromY = -28 * rNum;
          } else {
            const px = parseFloat(getComputedStyle(this).fontSize) || 16;
            fromY = -1.75 * px;
          }
        } catch (e) {}

        const tl = gsap.timeline({
          defaults: { ease: 'power3.out' },
          onComplete: () => {
            window.clearTimeout(readyFallback);
            markReady(root);
            const cleared = [logo, copy].filter(Boolean);
            gsap.set(cleared, { clearProps: 'opacity,visibility,y,transform' });
          },
        });

        if (logo) {
          tl.fromTo(logo, { autoAlpha: 0, y: fromY }, { autoAlpha: 1, y: 0, duration: 0.7 });
        }

        if (copy) {
          tl.fromTo(
            copy,
            { autoAlpha: 0, y: fromY * 1.15 },
            { autoAlpha: 1, y: 0, duration: 0.85 },
            logo ? '-=0.2' : 0
          );
        }

        this._ugMarathonKvTls.push(tl);
      });
    }

    parseYoutubeEmbed(url) {
      if (!url) return '';
      if (url.includes('youtube.com/shorts/')) {
        const id = url.split('/shorts/').pop().split('?')[0];
        return `https://www.youtube.com/embed/${id}`;
      }
      if (url.includes('youtu.be/')) {
        const id = url.split('youtu.be/').pop().split('?')[0];
        return `https://www.youtube.com/embed/${id}`;
      }
      if (url.includes('watch?v=')) {
        const id = url.split('v=').pop().split('&')[0];
        return `https://www.youtube.com/embed/${id}`;
      }
      return url;
    }

    stopSceneVideos(root) {
      root.querySelectorAll('ug-video-player').forEach((videoPlayer) => {
        if (typeof videoPlayer.reset === 'function') {
          videoPlayer.reset({ unload: true, disableAutoplay: true });
          return;
        }

        const videoElement = videoPlayer.querySelector('video');
        const iframeElement = videoPlayer.querySelector('iframe');

        if (videoElement) {
          try {
            videoElement.pause();
          } catch (e) {}
          videoElement.currentTime = 0;
        } else if (iframeElement) {
          try {
            iframeElement.contentWindow?.postMessage(
              '{"event":"command","func":"pauseVideo","args":""}',
              '*'
            );
          } catch (e) {}
        }

        const container = videoPlayer.querySelector('.ug-video__container');
        if (container) container.style.display = 'none';

        videoPlayer.querySelectorAll('.ug-video__poster').forEach((poster) => {
          poster.style.display = '';
        });

        videoPlayer.classList.remove('is-playing', 'is-loaded', 'has-started');
      });
    }

    isSafeImageUrl(url) {
      if (!url || typeof url !== 'string') return false;
      const trimmed = url.trim();
      if (!trimmed) return false;
      if (trimmed.startsWith('/') && !trimmed.startsWith('//')) return true;
      if (trimmed.startsWith('//')) {
        try {
          return new URL(`https:${trimmed}`).hostname.length > 0;
        } catch (e) {
          return false;
        }
      }
      try {
        const parsed = new URL(trimmed);
        return parsed.protocol === 'https:' || parsed.protocol === 'http:';
      } catch (e) {
        return false;
      }
    }

    createScenePlayButton() {
      const button = document.createElement('button');
      button.className = 'ug-video__play-button ug-marathon-scene__play-btn';
      button.type = 'button';
      button.setAttribute('aria-label', 'Watch Video');

      const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
      svg.setAttribute('xmlns', 'http://www.w3.org/2000/svg');
      svg.setAttribute('width', '24');
      svg.setAttribute('height', '24');
      svg.setAttribute('viewBox', '0 0 24 24');
      svg.setAttribute('fill', 'inherit');

      const path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
      path.setAttribute(
        'd',
        'M16.2666 11.2916C17.2427 11.7992 17.2462 12.4378 16.2666 13.0118L8.69734 17.7764C7.74627 18.2501 7.10033 17.9704 7.03252 16.9454L7.0004 6.97321C6.97899 6.02903 7.81229 5.76227 8.60633 6.21496L16.2666 11.2916Z',
      );
      path.setAttribute('stroke', 'inherit');
      path.setAttribute('stroke-width', '1.5');
      svg.appendChild(path);
      button.appendChild(svg);
      return button;
    }

    buildSceneStaticSlideElement(imgSrc) {
      if (!this.isSafeImageUrl(imgSrc)) return null;

      const wrap = document.createElement('div');
      wrap.className = 'ug-marathon-scene__image-wrap';

      const img = document.createElement('img');
      img.src = imgSrc.trim();
      img.alt = '';
      img.width = 315;
      img.height = 480;
      img.loading = 'lazy';
      img.decoding = 'async';

      wrap.appendChild(img);
      wrap.appendChild(this.createScenePlayButton());
      return wrap;
    }

    replaceSceneSlideWithStaticImage(slide, imgSrc) {
      const staticSlide = this.buildSceneStaticSlideElement(imgSrc);
      if (!staticSlide) return false;

      const player = slide.querySelector('ug-video-player');
      if (player) player.remove();
      slide.replaceChildren(staticSlide);
      slide.dataset.replaced = 'true';
      return true;
    }

    initSceneCarousel(section) {
      if (!(section instanceof HTMLElement)) return;
      if (section.dataset.ugMarathonSceneReady === '1') return;
      if (typeof window.Swiper !== 'function') return;

      const swiperEl = section.querySelector('[data-ug-marathon-scene-swiper]');
      if (!swiperEl) return;

      const autoplayDelay = parseInt(section.dataset.autoplayDelay || '9500', 10) || 9500;
      const progressCircle = section.querySelector(
        '.ug-marathon-scene__nav-btn--next .progress-ring__circle'
      );
      const currentEl = section.querySelector('[data-ug-marathon-scene-current]');
      const slideEls = section.querySelectorAll('.ug-marathon-scene__slide');
      const slideCount = slideEls.length;
      let inView = false;
      let autoPlayed = false;
      let slider = null;

      if (slideCount === 0) return;

      const modal = section.querySelector('[data-ug-marathon-scene-video-modal]');
      const videoIframe = section.querySelector('[data-ug-marathon-scene-video-iframe]');
      const videoEl = section.querySelector('[data-ug-marathon-scene-video-element]');
      const videoClose = section.querySelector('[data-ug-marathon-scene-video-close]');
      const videoBackdrop = section.querySelector('[data-ug-marathon-scene-video-backdrop]');

      const setProgressAnimating = (on) => {
        if (!progressCircle) return;
        progressCircle.classList.remove('is-animating');
        progressCircle.style.removeProperty('animation');
        progressCircle.style.removeProperty('animation-duration');
        if (!on) return;
        // Full circle fill duration == Swiper autoplay delay → finishes as next slide starts.
        void progressCircle.offsetWidth;
        progressCircle.style.setProperty('--ug-marathon-scene-progress-dur', autoplayDelay + 'ms');
        progressCircle.classList.add('is-animating');
      };

      const openVideoModal = (url) => {
        if (!modal || !videoIframe) return;
        const isMp4 = /\.mp4(\?|$)/i.test(url || '');
        let embed = this.parseYoutubeEmbed(url || '');

        modal.classList.add('is-open');
        modal.setAttribute('aria-hidden', 'false');

        if (isMp4 && videoEl) {
          videoIframe.src = '';
          videoIframe.style.display = 'none';
          try {
            videoEl.pause();
          } catch (e) {}
          videoEl.src = url;
          videoEl.style.display = 'block';
          videoEl.muted = true;
          videoEl.playsInline = true;
          window.setTimeout(() => {
            try {
              videoEl.play();
            } catch (e) {}
          }, 50);
        } else {
          const hasQuery = embed.indexOf('?') !== -1;
          const needsEnable = embed.indexOf('enablejsapi=1') === -1;
          const autoplayParams = 'autoplay=1&mute=1&playsinline=1';
          let final = embed;
          final += hasQuery
            ? '&' + autoplayParams + (needsEnable ? '&enablejsapi=1' : '')
            : '?' + autoplayParams + (needsEnable ? '&enablejsapi=1' : '');

          if (videoEl) {
            try {
              videoEl.pause();
            } catch (e) {}
            videoEl.style.display = 'none';
            videoEl.src = '';
          }
          videoIframe.src = final;
          videoIframe.style.display = 'block';
          window.setTimeout(() => {
            try {
              videoIframe.contentWindow?.postMessage(
                '{"event":"command","func":"playVideo","args":""}',
                '*'
              );
            } catch (e) {}
          }, 200);
        }

        try {
          if (slider && slider.autoplay) slider.autoplay.stop();
          const scrollbarComp = window.innerWidth - document.documentElement.clientWidth;
          document.documentElement.style.overflow = 'hidden';
          document.body.style.overflow = 'hidden';
          if (scrollbarComp > 0) {
            const currentPR =
              parseInt(getComputedStyle(document.body).paddingRight || '0', 10) || 0;
            document.body.dataset.prevPaddingRight = String(currentPR);
            document.body.style.paddingRight = currentPR + scrollbarComp + 'px';
          }
        } catch (e) {}
      };

      const closeVideoModal = () => {
        if (!modal || !videoIframe) return;
        try {
          if (videoEl) {
            videoEl.pause();
            videoEl.src = '';
            videoEl.style.display = 'none';
          }
        } catch (e) {}
        videoIframe.src = '';
        videoIframe.style.display = 'none';
        modal.classList.remove('is-open');
        modal.setAttribute('aria-hidden', 'true');
        try {
          document.documentElement.style.overflow = '';
          document.body.style.overflow = '';
          const prevPR = parseInt(document.body.dataset.prevPaddingRight || '0', 10) || 0;
          document.body.style.paddingRight = prevPR ? prevPR + 'px' : '';
          delete document.body.dataset.prevPaddingRight;
          if (slider && slider.autoplay && inView) slider.autoplay.start();
        } catch (e) {}
      };

      if (section._ugMarathonSceneModalAbort) {
        section._ugMarathonSceneModalAbort.abort();
      }
      const modalAbort = new AbortController();
      section._ugMarathonSceneModalAbort = modalAbort;
      const modalSignal = modalAbort.signal;

      if (videoClose) videoClose.addEventListener('click', closeVideoModal, { signal: modalSignal });
      if (videoBackdrop) {
        videoBackdrop.addEventListener('click', closeVideoModal, { signal: modalSignal });
      }

      const bindPlayButtons = () => {
        section.querySelectorAll('.ug-marathon-scene__play-btn').forEach((button) => {
          if (button.dataset.bound === '1') return;
          button.dataset.bound = '1';
          button.addEventListener('click', (event) => {
            event.preventDefault();
            event.stopPropagation();
            this.stopSceneVideos(section);

            const videoPlayer = button.closest('ug-video-player');
            let url = '';
            if (videoPlayer) {
              url =
                window.innerWidth <= 1023.98
                  ? videoPlayer.dataset.youtubeMobile || videoPlayer.dataset.videoMobile
                  : videoPlayer.dataset.youtubeDesktop || videoPlayer.dataset.videoDesktop;
            } else {
              const slide = button.closest('.ug-marathon-scene__slide');
              url = (slide && slide.dataset && slide.dataset.videoUrl) || '';
            }

            let finalUrl = url || '';
            if (!finalUrl) return;
            const isMp4 = /\.mp4(\?|$)/i.test(finalUrl);
            if (!isMp4 && finalUrl.indexOf('autoplay=1') === -1) {
              finalUrl =
                finalUrl.indexOf('?') !== -1
                  ? finalUrl + '&autoplay=1&mute=1&playsinline=1'
                  : finalUrl + '?autoplay=1&mute=1&playsinline=1';
            }
            openVideoModal(finalUrl);
          });
        });
      };

      const tryAutoplayFirstVideo = () => {
        if (autoPlayed || !slider) return;
        const activeSlide =
          (slider.slides && slider.slides[slider.activeIndex]) ||
          section.querySelector('.ug-marathon-scene__slide.swiper-slide-active');
        const videoPlayer = activeSlide ? activeSlide.querySelector('ug-video-player') : null;
        if (!videoPlayer) return;

        const playInline = () => {
          if (typeof videoPlayer.loadVideo === 'function') videoPlayer.loadVideo();
          if (typeof videoPlayer.mute === 'function') videoPlayer.mute();
          if (typeof videoPlayer.playVideo === 'function') {
            videoPlayer.playVideo();
            window.setTimeout(() => {
              try {
                if (!videoPlayer.classList.contains('is-playing')) {
                  const iframeEl = videoPlayer.querySelector('iframe');
                  const url =
                    window.innerWidth <= 1023.98
                      ? videoPlayer.dataset.youtubeMobile || videoPlayer.dataset.videoMobile
                      : videoPlayer.dataset.youtubeDesktop || videoPlayer.dataset.videoDesktop;
                  if (url) {
                    const needsAutoplay = url.indexOf('autoplay=1') === -1;
                    const finalUrl = needsAutoplay
                      ? url + (url.indexOf('?') !== -1 ? '&' : '?') + 'autoplay=1&mute=1&playsinline=1'
                      : url;
                    const container = videoPlayer.querySelector('.ug-video__container');
                    const posters = videoPlayer.querySelectorAll('.ug-video__poster');
                    if (container) container.style.display = 'block';
                    posters.forEach((p) => {
                      p.style.display = 'none';
                    });
                    if (iframeEl) iframeEl.src = finalUrl;
                    videoPlayer.classList.add('is-playing', 'is-loaded', 'has-started');
                  }
                }
              } catch (e) {}
              autoPlayed = true;
            }, 600);
          } else {
            autoPlayed = true;
          }
        };

        try {
          playInline();
        } catch (e) {}
      };

      const canLoop = slideCount > 1;
      const prevEl = section.querySelector('.ug-marathon-scene__nav-btn--prev');
      const nextEl = section.querySelector('.ug-marathon-scene__nav-btn--next');

      // slidesPerView: 'auto' + CSS width keeps slide size stable before/after init.
      // Do not pair loop with centeredSlidesBounds (Swiper: causes off-center + clone glitches).
      slider = new window.Swiper(swiperEl, {
        loop: canLoop,
        loopedSlides: canLoop ? slideCount : undefined,
        loopAdditionalSlides: canLoop ? 1 : 0,
        slidesPerView: 'auto',
        spaceBetween: 10,
        centeredSlides: true,
        centeredSlidesBounds: false,
        grabCursor: canLoop,
        watchSlidesProgress: true,
        observer: true,
        observeParents: true,
        autoplay: canLoop
          ? {
              delay: autoplayDelay,
              disableOnInteraction: false,
              pauseOnMouseEnter: false,
            }
          : false,
        navigation:
          canLoop && prevEl && nextEl
            ? {
                prevEl,
                nextEl,
              }
            : undefined,
        on: {
          init() {
            setProgressAnimating(false);
            try {
              this.autoplay?.stop?.();
            } catch (e) {}
          },
          afterInit() {
            try {
              this.update();
              if (canLoop && typeof this.slideToLoop === 'function') {
                this.slideToLoop(0, 0, false);
              }
              if (this.autoplay) {
                inView ? this.autoplay.start() : this.autoplay.stop();
              }
            } catch (e) {}
          },
          slideChangeTransitionStart() {
            setProgressAnimating(false);
          },
          slideChangeTransitionEnd() {
            setProgressAnimating(inView && canLoop);
          },
          slideChange: () => {
            if (!slider) return;
            const currentIndex = slider.realIndex + 1;
            if (currentEl) currentEl.textContent = String(currentIndex);

            // Always stop media when the active slide changes, including return to first.
            this.stopSceneVideos(section);
            if (slider.realIndex === 0) return;
            let replacedAny = false;
            section.querySelectorAll('.ug-marathon-scene__slide[data-video-url]').forEach((slide) => {
              if (slide.classList.contains('swiper-slide-duplicate')) return;
              if (slide.dataset.replaced === 'true') return;
              const imgSrc = slide.dataset.imgSrc || '';
              if (!imgSrc) return;
              if (this.replaceSceneSlideWithStaticImage(slide, imgSrc)) replacedAny = true;
            });
            if (replacedAny && canLoop) {
              try {
                slider.update();
              } catch (e) {}
            }
            bindPlayButtons();
          },
        },
      });

      section._ugMarathonSceneSlider = slider;
      section._ugMarathonSceneCloseModal = closeVideoModal;
      section.dataset.ugMarathonSceneReady = '1';
      bindPlayButtons();

      if ('IntersectionObserver' in window) {
        const io = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (entry.isIntersecting) {
                inView = true;
                try {
                  slider.autoplay?.start?.();
                } catch (e) {}
                if (progressCircle) {
                  progressCircle.classList.remove('is-animating');
                  void progressCircle.offsetWidth;
                  setProgressAnimating(true);
                }
                tryAutoplayFirstVideo();
              } else {
                inView = false;
                try {
                  slider.autoplay?.stop?.();
                } catch (e) {}
                setProgressAnimating(false);
              }
            });
          },
          { threshold: 0.3 }
        );
        io.observe(section);
        section._ugMarathonSceneIo = io;
      }
    }

    initPhotoGallery(section) {
      if (!section || section.dataset.ugMarathonGalleryReady === '1') return;
      const swiperEl = section.querySelector('[data-ug-marathon-gallery-swiper]');
      if (!swiperEl || typeof window.Swiper !== 'function') return;

      const prevEl = section.querySelector('.ug-marathon-gallery__nav-btn--prev');
      const nextEl = section.querySelector('.ug-marathon-gallery__nav-btn--next');
      const paginationEl = section.querySelector('[data-ug-marathon-gallery-pagination]');
      const slideCount = swiperEl.querySelectorAll('.ug-marathon-gallery__slide').length;
      if (!slideCount) return;

      section._ugMarathonGallerySlider = new window.Swiper(swiperEl, {
        slidesPerView: 'auto',
        spaceBetween: 7,
        watchOverflow: true,
        navigation:
          prevEl && nextEl
            ? {
                prevEl: prevEl,
                nextEl: nextEl,
              }
            : undefined,
        pagination:
          paginationEl && slideCount > 1
            ? {
                el: paginationEl,
                clickable: true,
              }
            : undefined,
        breakpoints: {
          1024: {
            spaceBetween: 20,
          },
        },
      });
      section.dataset.ugMarathonGalleryReady = '1';
    }

    bindPhotoGallery() {
      const boot = () => {
        this.querySelectorAll('[data-ug-marathon-gallery]').forEach((section) => {
          this.initPhotoGallery(section);
        });
      };

      if (typeof window.Swiper === 'function') {
        boot();
        return;
      }

      const existing = document.querySelector('script[src*="swiper"]');
      if (existing) {
        existing.addEventListener('load', boot, { once: true });
        window.setTimeout(boot, 1200);
        return;
      }
      window.setTimeout(boot, 0);
    }

    killPhotoGallery() {
      this.querySelectorAll('[data-ug-marathon-gallery]').forEach((section) => {
        if (section._ugMarathonGallerySlider) {
          try {
            section._ugMarathonGallerySlider.destroy(true, true);
          } catch (e) {}
          section._ugMarathonGallerySlider = null;
        }
        section.dataset.ugMarathonGalleryReady = '0';
      });
    }

    bindSceneCarousel() {
      const boot = () => {
        this.querySelectorAll('[data-ug-marathon-scene]').forEach((section) => {
          this.initSceneCarousel(section);
        });
      };

      if (typeof window.Swiper === 'function') {
        boot();
        return;
      }

      const existing = document.querySelector('script[src*="swiper"]');
      if (existing) {
        existing.addEventListener('load', boot, { once: true });
        window.setTimeout(boot, 1200);
        return;
      }
      window.setTimeout(boot, 0);
    }

    killSceneCarousel() {
      this.querySelectorAll('[data-ug-marathon-scene]').forEach((section) => {
        if (section._ugMarathonSceneModalAbort) {
          section._ugMarathonSceneModalAbort.abort();
          section._ugMarathonSceneModalAbort = null;
        }
        if (section._ugMarathonSceneCloseModal) {
          try {
            section._ugMarathonSceneCloseModal();
          } catch (e) {}
        }
        if (section._ugMarathonSceneIo) {
          section._ugMarathonSceneIo.disconnect();
          section._ugMarathonSceneIo = null;
        }
        if (section._ugMarathonSceneSlider) {
          try {
            section._ugMarathonSceneSlider.destroy(true, true);
          } catch (e) {}
          section._ugMarathonSceneSlider = null;
        }
        section.dataset.ugMarathonSceneReady = '0';
        section._ugMarathonSceneCloseModal = null;
      });
    }

    destroyProductsSwiper(section) {
      if (section && section._ugMarathonProductsSlider) {
        try {
          section._ugMarathonProductsSlider.destroy(true, true);
        } catch (e) {}
        section._ugMarathonProductsSlider = null;
      }
      if (section) {
        section.classList.remove('is-mb-swiper');
        const track = section.querySelector('.ug-marathon-products__track');
        if (track) {
          track.querySelectorAll(':scope > [data-ug-marathon-products-page]').forEach((page) => {
            while (page.firstElementChild) {
              const card = page.firstElementChild;
              card.classList.add('swiper-slide');
              track.insertBefore(card, page);
            }
            page.remove();
          });
        }
      }
    }

    startProductsSwiper(section) {
      const root = section.querySelector('[data-ug-marathon-products-swiper]');
      const paginationEl = section.querySelector('[data-ug-marathon-products-pagination]');
      if (!root || typeof window.Swiper !== 'function') return;
      this.destroyProductsSwiper(section);
      const track = root.querySelector('.ug-marathon-products__track');
      if (!track) return;
      const slides = Array.from(track.children).filter((el) =>
        el.classList.contains('ug-marathon-products__slide')
      );
      const perView = Math.max(1, Number(section.getAttribute('data-mb-per-view')) || 2);
      if (slides.length <= perView) return;

      for (let i = 0; i < slides.length; i += perView) {
        const page = document.createElement('div');
        page.className = 'swiper-slide ug-marathon-products__page';
        page.setAttribute('data-ug-marathon-products-page', '');
        track.insertBefore(page, slides[i]);
        slides.slice(i, i + perView).forEach((slide) => {
          slide.classList.remove('swiper-slide');
          page.appendChild(slide);
        });
      }
      section.classList.add('is-mb-swiper');

      section._ugMarathonProductsSlider = new window.Swiper(root, {
        direction: 'horizontal',
        slidesPerView: 1,
        slidesPerGroup: 1,
        spaceBetween: 12,
        autoHeight: true,
        allowTouchMove: true,
        simulateTouch: true,
        watchOverflow: true,
        pagination:
          paginationEl
            ? {
                el: paginationEl,
                clickable: true,
              }
            : undefined,
      });

      const slider = section._ugMarathonProductsSlider;
      const refresh = () => {
        if (slider && typeof slider.update === 'function') slider.update();
        if (slider && typeof slider.updateAutoHeight === 'function') slider.updateAutoHeight(0);
      };
      requestAnimationFrame(refresh);
      root.querySelectorAll('img').forEach((img) => {
        if (!img.complete) img.addEventListener('load', refresh, { once: true });
      });
    }

    initProductsSlider(section) {
      if (!(section instanceof HTMLElement)) return;
      if (section.dataset.ugMarathonProductsBound === '1') return;
      section.dataset.ugMarathonProductsBound = '1';

      const moreBtn = section.querySelector('[data-ug-marathon-products-more]');
      const moreText = section.querySelector('[data-ug-marathon-products-more-text]');
      const onMore = () => {
        const open = section.classList.toggle('is-expanded');
        if (moreText) {
          const next = open
            ? moreBtn.getAttribute('data-less-label')
            : moreBtn.getAttribute('data-more-label');
          if (next) moreText.textContent = next;
        }
      };
      if (moreBtn) moreBtn.addEventListener('click', onMore);

      const onCodeCopy = (e) => {
        const btn =
          e.target instanceof Element ? e.target.closest('[data-ug-marathon-code-copy]') : null;
        if (!btn || !section.contains(btn)) return;
        const code = btn.getAttribute('data-code');
        if (!code) return;
        const copiedText = btn.getAttribute('data-copied-text');
        const copyText = btn.getAttribute('data-copy-text');
        const onCopied = () => {
          if (copiedText) btn.textContent = copiedText;
          window.setTimeout(() => {
            if (copyText) btn.textContent = copyText;
          }, 2000);
        };
        if (navigator.clipboard && typeof navigator.clipboard.writeText === 'function') {
          navigator.clipboard.writeText(code).then(onCopied).catch(() => {});
        } else {
          const input = document.createElement('textarea');
          input.value = code;
          input.setAttribute('readonly', '');
          input.style.position = 'fixed';
          input.style.left = '-9999px';
          document.body.appendChild(input);
          input.select();
          try {
            document.execCommand('copy');
            onCopied();
          } catch (_) {}
          document.body.removeChild(input);
        }
      };
      section.addEventListener('click', onCodeCopy);

      const mq = window.matchMedia('(max-width: 1023.98px)');
      const syncSwiper = () => {
        this.destroyProductsSwiper(section);
        if (mq.matches) this.startProductsSwiper(section);
      };
      const swiperScript = document.querySelector('script[src*="swiper"]');
      let swiperRetryTimer = null;
      if (typeof window.Swiper !== 'function') {
        if (swiperScript) swiperScript.addEventListener('load', syncSwiper, { once: true });
        swiperRetryTimer = window.setTimeout(syncSwiper, 1200);
      }
      if (typeof mq.addEventListener === 'function') {
        mq.addEventListener('change', syncSwiper);
      } else if (typeof mq.addListener === 'function') {
        mq.addListener(syncSwiper);
      }
      syncSwiper();

      section._ugMarathonProductsCleanup = () => {
        if (moreBtn) moreBtn.removeEventListener('click', onMore);
        section.removeEventListener('click', onCodeCopy);
        if (typeof mq.removeEventListener === 'function') {
          mq.removeEventListener('change', syncSwiper);
        } else if (typeof mq.removeListener === 'function') {
          mq.removeListener(syncSwiper);
        }
        if (swiperScript) swiperScript.removeEventListener('load', syncSwiper);
        if (swiperRetryTimer) window.clearTimeout(swiperRetryTimer);
        this.destroyProductsSwiper(section);
      };
    }

    bindProducts() {
      this.querySelectorAll('[data-ug-marathon-products]').forEach((section) => {
        this.initProductsSlider(section);
      });
      if (this._ugMarathonProductCtaCleanups) {
        this._ugMarathonProductCtaCleanups.forEach((cleanup) => cleanup());
      }
      const ctaCleanups = [];
      if (window.UgProductCta && typeof window.UgProductCta.init === 'function') {
        window.UgProductCta.init(this, { cleanups: ctaCleanups });
      }
      this._ugMarathonProductCtaCleanups = ctaCleanups;
    }

    killProducts() {
      this.querySelectorAll('[data-ug-marathon-products]').forEach((section) => {
        if (typeof section._ugMarathonProductsCleanup === 'function') {
          section._ugMarathonProductsCleanup();
          section._ugMarathonProductsCleanup = null;
        }
        section.dataset.ugMarathonProductsBound = '0';
      });
      if (this._ugMarathonProductCtaCleanups) {
        this._ugMarathonProductCtaCleanups.forEach((cleanup) => cleanup());
        this._ugMarathonProductCtaCleanups = null;
      }
    }

    bindFeaturesMapTouch(scroller) {
      if (!(scroller instanceof HTMLElement)) return;
      if (scroller.dataset.ugMarathonMapTouchBound === '1') return;
      scroller.dataset.ugMarathonMapTouchBound = '1';

      let startX = 0;
      let startY = 0;
      let axis = null;

      const onStart = (event) => {
        const touch = event.touches && event.touches[0];
        if (!touch) return;
        axis = null;
        startX = touch.clientX;
        startY = touch.clientY;
        scroller.classList.remove('is-lock-y');
      };

      const onMove = (event) => {
        const touch = event.touches && event.touches[0];
        if (!touch || axis) return;
        const dx = Math.abs(touch.clientX - startX);
        const dy = Math.abs(touch.clientY - startY);
        if (dx < 8 && dy < 8) return;
        axis = dy > dx ? 'y' : 'x';
        if (axis === 'y') scroller.classList.add('is-lock-y');
      };

      const onEnd = () => {
        axis = null;
        scroller.classList.remove('is-lock-y');
      };

      scroller.addEventListener('touchstart', onStart, { passive: true });
      scroller.addEventListener('touchmove', onMove, { passive: true });
      scroller.addEventListener('touchend', onEnd, { passive: true });
      scroller.addEventListener('touchcancel', onEnd, { passive: true });

      scroller._ugMarathonMapTouchCleanup = () => {
        scroller.removeEventListener('touchstart', onStart);
        scroller.removeEventListener('touchmove', onMove);
        scroller.removeEventListener('touchend', onEnd);
        scroller.removeEventListener('touchcancel', onEnd);
        scroller.classList.remove('is-lock-y');
        scroller.dataset.ugMarathonMapTouchBound = '0';
        scroller._ugMarathonMapTouchCleanup = null;
      };
    }

    centerFeaturesMap(section) {
      if (!(section instanceof HTMLElement)) return;
      const scroller = section.querySelector(
        '[data-ug-marathon-features-map] .ug-marathon-features__map-scroller'
      );
      const img = scroller && scroller.querySelector('.ug-marathon-features__map-img');
      if (!(scroller instanceof HTMLElement) || !(img instanceof HTMLElement)) return;

      this.bindFeaturesMapTouch(scroller);

      const center = () => {
        const max = scroller.scrollWidth - scroller.clientWidth;
        if (max <= 0) return;
        scroller.scrollLeft = max / 2;
      };

      const run = () => {
        window.requestAnimationFrame(() => {
          center();
          window.requestAnimationFrame(center);
        });
      };

      if (img.complete && img.naturalWidth > 0) {
        run();
      } else {
        img.addEventListener('load', run, { once: true });
      }
    }

    initEventFeatures(section) {
      if (!(section instanceof HTMLElement)) return;
      if (section.dataset.ugMarathonFeaturesBound === '1') return;
      section.dataset.ugMarathonFeaturesBound = '1';

      const tabs = Array.from(section.querySelectorAll('[data-ug-marathon-features-tab]'));
      const panels = Array.from(section.querySelectorAll('[data-ug-marathon-features-panel]'));
      if (!tabs.length || !panels.length) return;

      const keys = tabs
        .map((tab) => tab.getAttribute('data-ug-marathon-features-tab'))
        .filter(Boolean);
      let activeKey = keys[0] || '1';
      const autoplayEnabled =
        section.getAttribute('data-tab-autoplay') !== 'false' &&
        !ugMarathonPrefersReducedMotion();
      const delayMs = Math.max(
        2000,
        Number(section.getAttribute('data-tab-autoplay-delay') || 5000) || 5000
      );
      const cleanups = [];

      let hovered = false;
      let inView = true;

      const setPaused = (next) => {
        section.classList.toggle('is-tab-paused', !!next);
      };

      const syncPause = () => setPaused(hovered || !inView);

      const clearProgress = () => {
        tabs.forEach((tab) => {
          tab.classList.remove('is-progressing');
          tab.style.removeProperty('--ug-marathon-tab-duration');
        });
      };

      const startProgress = (tab) => {
        if (!(tab instanceof HTMLElement) || !autoplayEnabled || keys.length < 2) return;
        // Manual click happens while cursor is over tabs — don't keep progress frozen at 0.
        hovered = false;
        syncPause();
        tab.style.setProperty('--ug-marathon-tab-duration', delayMs + 'ms');
        tab.classList.remove('is-progressing');
        void tab.offsetWidth;
        tab.classList.add('is-progressing');
      };

      const activate = (key, { restart = true, scrollTab = false } = {}) => {
        const next = String(key || keys[0] || '1');
        activeKey = next;
        clearProgress();

        tabs.forEach((tab) => {
          const active = tab.getAttribute('data-ug-marathon-features-tab') === next;
          tab.classList.toggle('is-active', active);
          tab.setAttribute('aria-selected', active ? 'true' : 'false');
          if (active && scrollTab) {
            try {
              tab.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
            } catch (e) {}
          }
        });
        panels.forEach((panel) => {
          const active = panel.getAttribute('data-ug-marathon-features-panel') === next;
          panel.classList.toggle('is-active', active);
          if (active) {
            panel.removeAttribute('hidden');
          } else {
            panel.setAttribute('hidden', '');
          }
        });
        if (next === '1') this.centerFeaturesMap(section);
        if (next === '4') this.ensureEventFeaturesExperience(section);

        if (autoplayEnabled && restart) {
          const activeTab = tabs.find(
            (tab) => tab.getAttribute('data-ug-marathon-features-tab') === next
          );
          startProgress(activeTab);
        } else if (!autoplayEnabled) {
          // Keep full underline when autoplay is off.
        }
      };

      tabs.forEach((tab) => {
        const onClick = () => {
          activate(tab.getAttribute('data-ug-marathon-features-tab'), {
            restart: true,
            scrollTab: true,
          });
        };
        const onAnimEnd = (event) => {
          if (event.animationName !== 'ugMarathonTabProgress') return;
          if (!tab.classList.contains('is-active') || !tab.classList.contains('is-progressing')) {
            return;
          }
          if (!autoplayEnabled || keys.length < 2) return;
          const idx = Math.max(0, keys.indexOf(activeKey));
          activate(keys[(idx + 1) % keys.length], { restart: true, scrollTab: false });
        };
        tab.addEventListener('click', onClick);
        tab.addEventListener('animationend', onAnimEnd);
        cleanups.push(() => {
          tab.removeEventListener('click', onClick);
          tab.removeEventListener('animationend', onAnimEnd);
        });
      });

      if (autoplayEnabled && keys.length > 1) {
        // Pause only over panel content — hovering tabs must not freeze progress at 0.
        const panelsRoot = section.querySelector('.ug-marathon-features__panels');
        const hoverTarget = panelsRoot instanceof HTMLElement ? panelsRoot : null;

        if (hoverTarget) {
          const onEnter = () => {
            hovered = true;
            syncPause();
          };
          const onLeave = () => {
            hovered = false;
            syncPause();
          };
          hoverTarget.addEventListener('mouseenter', onEnter);
          hoverTarget.addEventListener('mouseleave', onLeave);
          cleanups.push(() => {
            hoverTarget.removeEventListener('mouseenter', onEnter);
            hoverTarget.removeEventListener('mouseleave', onLeave);
          });
        }

        if (typeof IntersectionObserver === 'function') {
          const io = new IntersectionObserver(
            (entries) => {
              const entry = entries[0];
              inView = !!(entry && entry.isIntersecting);
              syncPause();
            },
            { threshold: 0.25 }
          );
          io.observe(section);
          cleanups.push(() => io.disconnect());
        }
      }

      section._ugMarathonFeaturesActivate = activate;
      section._ugMarathonFeaturesCleanup = () => {
        setPaused(false);
        clearProgress();
        cleanups.forEach((fn) => {
          try {
            fn();
          } catch (e) {}
        });
      };

      activate(keys[0] || '1', { restart: true, scrollTab: false });
    }

    ensureEventFeaturesExperience(section) {
      if (!(section instanceof HTMLElement)) return;
      const root = section.querySelector('[data-ug-marathon-features-experience]');
      if (!(root instanceof HTMLElement)) return;
      if (typeof window.Swiper !== 'function') return;

      const isMobile = window.matchMedia('(max-width: 1023.98px)').matches;
      if (!isMobile) {
        if (section._ugMarathonFeaturesExpSwiper) {
          try {
            section._ugMarathonFeaturesExpSwiper.destroy(true, true);
          } catch (e) {}
          section._ugMarathonFeaturesExpSwiper = null;
        }
        return;
      }

      if (section._ugMarathonFeaturesExpSwiper) {
        try {
          section._ugMarathonFeaturesExpSwiper.update();
        } catch (e) {}
        return;
      }

      const dots = root.querySelector('[data-ug-marathon-features-exp-dots]');
      // Figma MB: 335 card on 375 viewport, gap 12 → centered with prev/next peek.
      section._ugMarathonFeaturesExpSwiper = new window.Swiper(root, {
        slidesPerView: 'auto',
        centeredSlides: true,
        spaceBetween: 12,
        grabCursor: true,
        watchOverflow: true,
        pagination: dots
          ? {
              el: dots,
              clickable: true,
            }
          : undefined,
      });
    }

    bindEventFeatures() {
      const boot = () => {
        this.querySelectorAll('[data-ug-marathon-features]').forEach((section) => {
          this.initEventFeatures(section);
        });
      };
      boot();

      if (!this._ugMarathonFeaturesResize) {
        this._ugMarathonFeaturesResize = ugMarathonRafThrottle(() => {
          this.querySelectorAll('[data-ug-marathon-features]').forEach((section) => {
            const active = section.querySelector(
              '[data-ug-marathon-features-panel].is-active'
            );
            const activeKey = active && active.getAttribute('data-ug-marathon-features-panel');
            if (activeKey === '1') {
              this.centerFeaturesMap(section);
            }
            if (activeKey === '4') {
              this.ensureEventFeaturesExperience(section);
            } else if (
              section._ugMarathonFeaturesExpSwiper &&
              !window.matchMedia('(max-width: 1023.98px)').matches
            ) {
              try {
                section._ugMarathonFeaturesExpSwiper.destroy(true, true);
              } catch (e) {}
              section._ugMarathonFeaturesExpSwiper = null;
            }
          });
        });
        window.addEventListener('resize', this._ugMarathonFeaturesResize, { passive: true });
      }
    }

    killEventFeatures() {
      if (this._ugMarathonFeaturesResize) {
        window.removeEventListener('resize', this._ugMarathonFeaturesResize);
        this._ugMarathonFeaturesResize = null;
      }

      this.querySelectorAll('[data-ug-marathon-features]').forEach((section) => {
        if (typeof section._ugMarathonFeaturesCleanup === 'function') {
          try {
            section._ugMarathonFeaturesCleanup();
          } catch (e) {}
          section._ugMarathonFeaturesCleanup = null;
        }
        section
          .querySelectorAll('[data-ug-marathon-features-map] .ug-marathon-features__map-scroller')
          .forEach((scroller) => {
            if (typeof scroller._ugMarathonMapTouchCleanup === 'function') {
              try {
                scroller._ugMarathonMapTouchCleanup();
              } catch (e) {}
            }
          });
        if (section._ugMarathonFeaturesExpSwiper) {
          try {
            section._ugMarathonFeaturesExpSwiper.destroy(true, true);
          } catch (e) {}
          section._ugMarathonFeaturesExpSwiper = null;
        }
        section.dataset.ugMarathonFeaturesBound = '0';
        section._ugMarathonFeaturesActivate = null;
      });
    }

    bindReveal() {
      const targets = Array.from(this.querySelectorAll('[data-ug-marathon-reveal]'));
      if (!targets.length) return;

      if (ugMarathonPrefersReducedMotion()) {
        targets.forEach((el) => el.classList.add('is-revealed'));
        return;
      }

      const gsap = window.gsap;
      const ScrollTrigger = window.ScrollTrigger;
      if (!gsap || !ScrollTrigger) {
        targets.forEach((el) => el.classList.add('is-revealed'));
        return;
      }

      ugMarathonEnsureGsapRegistered();
      if (!this._ugMarathonRevealTweens) this._ugMarathonRevealTweens = [];

      targets.forEach((el) => {
        if (!(el instanceof HTMLElement)) return;
        if (el.dataset.ugMarathonRevealBound === '1') return;
        el.dataset.ugMarathonRevealBound = '1';

        gsap.set(el, { autoAlpha: 0, y: 28 });
        const tween = gsap.to(el, {
          autoAlpha: 1,
          y: 0,
          duration: 0.75,
          ease: 'power2.out',
          onComplete: () => el.classList.add('is-revealed'),
          scrollTrigger: {
            trigger: el,
            start: 'top 88%',
            toggleActions: 'play none none none',
            once: true,
          },
        });
        this._ugMarathonRevealTweens.push(tween);
      });
    }

    killReveal() {
      if (this._ugMarathonRevealTweens && this._ugMarathonRevealTweens.length) {
        this._ugMarathonRevealTweens.forEach((tween) => {
          if (tween && tween.scrollTrigger) tween.scrollTrigger.kill();
          if (tween) tween.kill();
        });
        this._ugMarathonRevealTweens = [];
      }

      this.querySelectorAll('[data-ug-marathon-reveal]').forEach((el) => {
        el.dataset.ugMarathonRevealBound = '0';
        el.classList.remove('is-revealed');
        if (window.gsap) {
          window.gsap.set(el, { clearProps: 'opacity,visibility,transform,y' });
        } else {
          el.style.opacity = '';
          el.style.visibility = '';
          el.style.transform = '';
        }
      });
    }

    findFooterEl() {
      if (this._ugMarathonFooterEl && document.contains(this._ugMarathonFooterEl)) {
        return this._ugMarathonFooterEl;
      }

      const footerTag =
        document.querySelector('footer.ug-footer') ||
        document.querySelector('footer') ||
        document.querySelector('.t4s-footer');
      if (footerTag && footerTag.offsetHeight > 24) {
        this._ugMarathonFooterEl = footerTag;
        return footerTag;
      }

      const sectionNodes = document.querySelectorAll('[id^="shopify-section-"][id*="footer"]');
      for (let i = 0; i < sectionNodes.length; i += 1) {
        const section = sectionNodes[i];
        const inner = section.querySelector('footer');
        if (inner && inner.offsetHeight > 24) {
          this._ugMarathonFooterEl = inner;
          return inner;
        }
        if (section.offsetHeight > 80) {
          this._ugMarathonFooterEl = section;
          return section;
        }
      }

      const group = document.querySelector('.shopify-section-group-footer-group');
      if (group && group.offsetHeight > 24) {
        this._ugMarathonFooterEl = group;
        return group;
      }

      this._ugMarathonFooterEl = null;
      return null;
    }

    bindStickyBarScroll(bar) {
      if (!bar) return;
      bar.querySelectorAll('[data-ug-marathon-sticky-scroll], a.ug-marathon-sticky-bar__btn[href^="#"]').forEach(
        (btn) => {
          if (btn._ugMarathonStickyScrollBound) return;
          btn._ugMarathonStickyScrollBound = true;
          btn.addEventListener('click', (event) => {
            const href = btn.getAttribute('href');
            if (!href || href.charAt(0) !== '#') return;
            const didScroll = this.smoothScrollToTarget(href);
            if (!didScroll) return;
            event.preventDefault();
            const target = this.resolveScrollTarget(href);
            const emailInput =
              target && target.querySelector('.ug-marathon-giveaway__input-email, input[type="email"]');
            if (emailInput) {
              window.setTimeout(() => {
                try {
                  emailInput.focus({ preventScroll: true });
                } catch (e) {
                  emailInput.focus();
                }
              }, 450);
            }
          });
        }
      );
    }

    syncStickyBarScaleToken(bar) {
      if (!(bar instanceof HTMLElement)) return;
      // Docked bar leaves .ug-marathon — copy scale token so 版心 calc() still resolves.
      let token = '';
      try {
        token = getComputedStyle(this).getPropertyValue('--ug-marathon-kv-r').trim();
      } catch (e) {}
      if (token) {
        bar.style.setProperty('--ug-marathon-kv-r', token);
      } else {
        bar.style.removeProperty('--ug-marathon-kv-r');
      }
    }

    dockStickyBarToFooter(bar, footer) {
      if (!bar || !footer || !footer.parentNode) return;
      if (bar.dataset.ugMarathonStickyDocked === '1') return;

      // Mode switch must not animate — that was the "footer left, bar jumps later" lag.
      bar.style.transition = 'none';
      this.syncStickyBarScaleToken(bar);
      bar.classList.add('is-visible', 'is-docked');
      this.classList.add('ug-marathon--sticky-docked');
      footer.parentNode.insertBefore(bar, footer);
      bar.dataset.ugMarathonStickyDocked = '1';
      this._ugMarathonStickyVisible = true;
      void bar.offsetHeight;
      bar.style.removeProperty('transition');
    }

    undockStickyBar(bar, slot) {
      if (!bar || bar.dataset.ugMarathonStickyDocked !== '1') return;

      bar.style.transition = 'none';
      bar.classList.remove('is-docked');
      this.classList.remove('ug-marathon--sticky-docked');
      delete bar.dataset.ugMarathonStickyDocked;
      if (slot && bar.parentNode !== slot) slot.appendChild(bar);
      this.syncStickyBarScaleToken(bar);
      void bar.offsetHeight;
      bar.style.removeProperty('transition');
    }

    updateStickyBar() {
      const bar = this._ugMarathonStickyBarEl;
      const slot = this._ugMarathonStickyBarSlot;
      if (!bar || !slot) return;

      const kv = this.querySelector('[data-ug-marathon-kv]');
      const shouldShow = !!(kv && kv.getBoundingClientRect().bottom < 0);
      const docked = bar.dataset.ugMarathonStickyDocked === '1';

      if (!shouldShow) {
        if (docked) this.undockStickyBar(bar, slot);
        if (this._ugMarathonStickyVisible) {
          this._ugMarathonStickyVisible = false;
          bar.classList.remove('is-visible');
        }
        return;
      }

      if (!this._ugMarathonStickyVisible) {
        this._ugMarathonStickyVisible = true;
        bar.classList.add('is-visible');
      }

      const footer = this.findFooterEl();
      const vh = window.innerHeight || 0;
      if (!footer || vh <= 0) {
        if (docked) this.undockStickyBar(bar, slot);
        return;
      }

      const footerTop = footer.getBoundingClientRect().top;
      const barH = bar.offsetHeight || 85;

      // Dock once into document flow above footer (scrolls with page → no chase lag).
      // Undock with hysteresis so the threshold doesn't flip-flop.
      if (!docked && footerTop < vh) {
        this.dockStickyBarToFooter(bar, footer);
        return;
      }
      if (docked && footerTop > vh + barH) {
        this.undockStickyBar(bar, slot);
      }
    }

    initStickyBar() {
      // Prevent duplicate listeners on Theme Editor remounts.
      this.killStickyBar();

      const slot = this.querySelector('[data-ug-marathon-sticky-bar-slot]');
      let bar = this.querySelector('[data-ug-marathon-sticky-bar]');

      // Recover / remove orphans left on body or before footer from older logic.
      document.querySelectorAll('body > [data-ug-marathon-sticky-bar]').forEach((orphan) => {
        if (slot && (!bar || orphan === bar)) {
          slot.appendChild(orphan);
          bar = orphan;
        } else if (orphan !== bar) {
          orphan.remove();
        }
      });

      if (!bar || !slot) {
        this.classList.remove('ug-marathon--has-sticky-bar', 'ug-marathon--sticky-docked');
        return;
      }

      if (bar.parentNode !== slot) {
        slot.appendChild(bar);
      }

      this._ugMarathonStickyBarEl = bar;
      this._ugMarathonStickyBarSlot = slot;
      this._ugMarathonStickyVisible = false;
      this.classList.add('ug-marathon--has-sticky-bar');

      bar.classList.remove('is-visible', 'is-docked');
      delete bar.dataset.ugMarathonStickyDocked;
      bar.style.bottom = '0px';
      bar.style.removeProperty('--ug-marathon-sticky-footer-push');
      bar.removeAttribute('data-ug-marathon-sticky-owner');
      bar.style.removeProperty('transition');
      this.syncStickyBarScaleToken(bar);

      this.bindStickyBarScroll(bar);

      let ticking = false;
      const onScroll = () => {
        if (ticking) return;
        ticking = true;
        window.requestAnimationFrame(() => {
          this.updateStickyBar();
          ticking = false;
        });
      };
      const onResize = () => {
        this.syncStickyBarScaleToken(bar);
        this.updateStickyBar();
      };

      this._ugMarathonStickyBarScroll = onScroll;
      this._ugMarathonStickyBarResize = onResize;
      window.addEventListener('scroll', onScroll, { passive: true });
      window.addEventListener('resize', onResize, { passive: true });
      this.updateStickyBar();
    }

    killStickyBar() {
      const bar =
        this._ugMarathonStickyBarEl ||
        this.querySelector('[data-ug-marathon-sticky-bar]') ||
        document.querySelector('[data-ug-marathon-sticky-bar]');
      const slot = this._ugMarathonStickyBarSlot || this.querySelector('[data-ug-marathon-sticky-bar-slot]');

      if (this._ugMarathonStickyBarScroll) {
        window.removeEventListener('scroll', this._ugMarathonStickyBarScroll);
        this._ugMarathonStickyBarScroll = null;
      }
      if (this._ugMarathonStickyBarResize) {
        window.removeEventListener('resize', this._ugMarathonStickyBarResize);
        this._ugMarathonStickyBarResize = null;
      }

      if (bar) {
        this.undockStickyBar(bar, slot);
        bar.classList.remove('is-visible', 'is-docked');
        delete bar.dataset.ugMarathonStickyDocked;
        bar.style.bottom = '0px';
        bar.style.removeProperty('--ug-marathon-sticky-footer-push');
        bar.style.removeProperty('--ug-marathon-kv-r');
        bar.style.removeProperty('transition');
        bar.removeAttribute('data-ug-marathon-sticky-owner');
        if (slot && bar.parentNode !== slot) slot.appendChild(bar);
      }

      this.classList.remove('ug-marathon--has-sticky-bar', 'ug-marathon--sticky-docked');
      this._ugMarathonStickyBarEl = null;
      this._ugMarathonStickyBarSlot = null;
      this._ugMarathonFooterEl = null;
      this._ugMarathonStickyVisible = false;
      this._ugMarathonStickyBottom = null;
    }

    splitStoryTitleChars(titleEl) {
      if (!(titleEl instanceof HTMLElement)) return [];
      if (titleEl.dataset.ugMarathonStorySplit === '1') {
        return Array.from(titleEl.querySelectorAll('.ug-marathon-story__char'));
      }

      const text = titleEl.textContent
        .replace(/\r\n/g, '\n')
        .replace(/\r/g, '\n')
        .replace(/[ \t]+/g, ' ')
        .replace(/ *\n */g, '\n')
        .replace(/^\n+|\n+$/g, '');
      const ariaLabel = text.replace(/\n+/g, ' ').trim();
      titleEl.setAttribute('aria-label', ariaLabel);
      titleEl.textContent = '';

      const frag = document.createDocumentFragment();
      for (const char of text) {
        if (char === '\n') {
          frag.appendChild(document.createElement('br'));
          continue;
        }
        if (char === ' ') {
          frag.appendChild(document.createTextNode(' '));
          continue;
        }
        const span = document.createElement('span');
        span.className = 'ug-marathon-story__char';
        span.textContent = char;
        span.setAttribute('aria-hidden', 'true');
        frag.appendChild(span);
      }
      titleEl.appendChild(frag);
      titleEl.dataset.ugMarathonStorySplit = '1';
      return Array.from(titleEl.querySelectorAll('.ug-marathon-story__char'));
    }

    initStorySection(section) {
      if (!(section instanceof HTMLElement)) return;
      if (section.dataset.ugMarathonStoryBound === '1') return;
      section.dataset.ugMarathonStoryBound = '1';

      const titleEls = Array.from(section.querySelectorAll('[data-ug-marathon-story-title]'));
      const titleWrap = section.querySelector('.ug-marathon-story__title-wrap');
      const iconEl = section.querySelector('[data-ug-marathon-story-icon]');
      if (!titleEls.length && !iconEl) return;

      const titleGroups = titleEls.map((titleEl) => this.splitStoryTitleChars(titleEl));
      const triggerEl = titleWrap || titleEls[0];

      if (ugMarathonPrefersReducedMotion()) {
        titleGroups.forEach((chars) => {
          chars.forEach((char) => {
            char.style.color = '#fff';
          });
        });
        if (iconEl) iconEl.style.transform = 'rotate(5deg)';
        return;
      }

      const gsap = window.gsap;
      const ScrollTrigger = window.ScrollTrigger;
      if (!gsap || !ScrollTrigger) {
        titleGroups.forEach((chars) => {
          chars.forEach((char) => {
            char.style.color = '#fff';
          });
        });
        if (iconEl) iconEl.style.transform = 'rotate(5deg)';
        return;
      }

      ugMarathonEnsureGsapRegistered();

      // PC / MB titles share the same scrub range; each keeps its own line breaks.
      // fromTo keeps gray↔white endpoints correct after mid-page refresh (progress already 1).
      if (!this._ugMarathonStoryTweens) this._ugMarathonStoryTweens = [];
      titleGroups.forEach((chars) => {
        if (!chars.length || !triggerEl) return;

        const tl = gsap.timeline({
          defaults: { ease: 'none' },
          scrollTrigger: {
            trigger: triggerEl,
            start: 'top 80%',
            end: 'top 35%',
            scrub: true,
            invalidateOnRefresh: true,
          },
        });

        const step = 0.12;
        chars.forEach((char, index) => {
          tl.fromTo(
            char,
            { color: '#666' },
            { color: '#fff', duration: step, immediateRender: false },
            index * step
          );
        });

        this._ugMarathonStoryTweens.push(tl);
      });

      // Icon: rotate forward on enter; reverse when scrolling back past start.
      if (iconEl) {
        gsap.set(iconEl, { rotation: -6, transformOrigin: '50% 50%' });
        const iconTween = gsap.to(iconEl, {
          rotation: 5,
          duration: 0.55,
          ease: 'power2.out',
          scrollTrigger: {
            trigger: iconEl,
            start: 'center center',
            toggleActions: 'play none none reverse',
          },
        });
        if (!this._ugMarathonStoryTweens) this._ugMarathonStoryTweens = [];
        this._ugMarathonStoryTweens.push(iconTween);
      }

      // Browser scroll restoration / late images can desync scrub progress after mid-page refresh.
      const refreshST = () => {
        if (!window.ScrollTrigger) return;
        window.ScrollTrigger.refresh();
        window.ScrollTrigger.update();
      };
      window.requestAnimationFrame(() => {
        refreshST();
        window.requestAnimationFrame(refreshST);
      });
      if (!this._ugMarathonStoryRefreshBound) {
        this._ugMarathonStoryRefreshBound = true;
        this._ugMarathonStoryRefreshST = refreshST;
        window.addEventListener('load', refreshST, { passive: true });
        window.addEventListener('pageshow', refreshST, { passive: true });
      }
      section.querySelectorAll('img').forEach((img) => {
        if (img.complete) return;
        img.addEventListener('load', refreshST, { once: true });
      });
    }

    bindStory() {
      this.querySelectorAll('[data-ug-marathon-story]').forEach((section) => {
        this.initStorySection(section);
      });
    }

    killStory() {
      if (this._ugMarathonStoryTweens && this._ugMarathonStoryTweens.length) {
        this._ugMarathonStoryTweens.forEach((tween) => {
          if (tween && tween.scrollTrigger) tween.scrollTrigger.kill();
          if (tween) tween.kill();
        });
        this._ugMarathonStoryTweens = [];
      }

      if (this._ugMarathonStoryRefreshST) {
        window.removeEventListener('load', this._ugMarathonStoryRefreshST);
        window.removeEventListener('pageshow', this._ugMarathonStoryRefreshST);
        this._ugMarathonStoryRefreshST = null;
        this._ugMarathonStoryRefreshBound = false;
      }

      this.querySelectorAll('[data-ug-marathon-story]').forEach((section) => {
        section.dataset.ugMarathonStoryBound = '0';
        section.querySelectorAll('.ug-marathon-story__char').forEach((char) => {
          if (window.gsap) {
            window.gsap.set(char, { clearProps: 'color' });
          } else {
            char.style.color = '';
          }
        });
        const iconEl = section.querySelector('[data-ug-marathon-story-icon]');
        if (iconEl) {
          if (window.gsap) {
            window.gsap.set(iconEl, { clearProps: 'transform,rotation' });
          } else {
            iconEl.style.transform = '';
          }
        }
      });
    }

    getScrollOffset() {
      const stickyNav = this.querySelector('[data-ug-marathon-nav]');
      const stickyHeight = stickyNav ? stickyNav.offsetHeight || 0 : 0;
      return stickyHeight + 6;
    }

    resolveScrollTarget(rawTarget) {
      if (!rawTarget) return null;
      const id = String(rawTarget).replace(/^#/, '');
      if (!id) return null;
      return this.querySelector('#' + CSS.escape(id)) || document.getElementById(id);
    }

    smoothScrollToTarget(rawTarget) {
      const targetEl = this.resolveScrollTarget(rawTarget);
      if (!targetEl) return false;
      const offset = this.getScrollOffset();
      const targetPos = targetEl.getBoundingClientRect().top + window.pageYOffset - offset;
      window.scrollTo({ top: Math.max(0, targetPos), behavior: 'smooth' });
      return true;
    }

    scrollActiveLinkIntoView(link, navInner, behavior = 'smooth') {
      if (!(link instanceof HTMLElement) || !(navInner instanceof HTMLElement)) return;
      if (window.innerWidth > 1023.98) return;
      const linkLeft = link.offsetLeft;
      const linkWidth = link.offsetWidth;
      const viewWidth = navInner.clientWidth;
      const targetScroll = linkLeft - (viewWidth - linkWidth) / 2;
      navInner.scrollTo({ left: Math.max(0, targetScroll), behavior });
    }

    keepActiveLinkVisible(link, navInner) {
      if (!(link instanceof HTMLElement) || !(navInner instanceof HTMLElement)) return;
      if (window.innerWidth > 1023.98) return;
      const edgeGap = 8;
      const currentLeft = navInner.scrollLeft;
      const viewRight = currentLeft + navInner.clientWidth;
      const linkLeft = link.offsetLeft;
      const linkRight = linkLeft + link.offsetWidth;
      let targetLeft = currentLeft;
      if (linkLeft < currentLeft + edgeGap) {
        targetLeft = linkLeft - edgeGap;
      } else if (linkRight > viewRight - edgeGap) {
        targetLeft = linkRight - navInner.clientWidth + edgeGap;
      }
      if (Math.abs(targetLeft - currentLeft) > 1) {
        navInner.scrollTo({ left: Math.max(0, targetLeft), behavior: 'smooth' });
      }
    }

    initStickyNav() {
      const stickyNav = this.querySelector('[data-ug-marathon-nav]');
      if (!stickyNav) return;
      if (stickyNav.dataset.ugMarathonNavBound === '1') return;
      stickyNav.dataset.ugMarathonNavBound = '1';

      const navInner = stickyNav.querySelector('.ug-marathon-nav__inner');
      const links = stickyNav.querySelectorAll('.ug-marathon-nav__link');
      if (!links.length) return;

      const mobileNavMq = window.matchMedia('(max-width: 1023.98px)');
      const navPlaceholder = document.createElement('div');
      navPlaceholder.className = 'ug-marathon-nav__placeholder';
      navPlaceholder.setAttribute('aria-hidden', 'true');
      navPlaceholder.style.height = '0px';
      stickyNav.before(navPlaceholder);

      const syncMobileFixedState = () => {
        if (!mobileNavMq.matches) {
          stickyNav.classList.remove('is-nav-fixed');
          navPlaceholder.style.height = '0px';
          return;
        }
        const markerTop = navPlaceholder.getBoundingClientRect().top + window.pageYOffset;
        const shouldFix = window.pageYOffset >= markerTop;
        stickyNav.classList.toggle('is-nav-fixed', shouldFix);
        navPlaceholder.style.height = shouldFix ? `${stickyNav.offsetHeight}px` : '0px';
      };

      const sections = [];
      links.forEach((link) => {
        const targetId = link.getAttribute('data-target') || link.getAttribute('href');
        const targetEl = this.resolveScrollTarget(targetId);
        if (targetEl) sections.push({ element: targetEl, link });
      });

      const navAbort = new AbortController();
      this._ugMarathonNavAbort = navAbort;
      const { signal } = navAbort;

      links.forEach((link) => {
        link.addEventListener(
          'click',
          (event) => {
            const targetId = link.getAttribute('data-target') || link.getAttribute('href');
            const didScroll = this.smoothScrollToTarget(targetId);
            if (!didScroll) return;
            event.preventDefault();
            this.scrollActiveLinkIntoView(link, navInner, 'smooth');
          },
          { signal }
        );
      });

      const lastSectionEl = sections.length ? sections[sections.length - 1].element : null;
      let activeLink = null;
      let navUpdateFrame = 0;

      const updateActiveLink = () => {
        navUpdateFrame = 0;
        syncMobileFixedState();
        const viewportOffset = this.getScrollOffset();
        let currentSection = null;

        sections.forEach((section) => {
          const rect = section.element.getBoundingClientRect();
          if (rect.top <= viewportOffset + 2) currentSection = section;
        });

        const nextActive = currentSection ? currentSection.link : links[0] || null;
        const activeChanged = nextActive !== activeLink;

        if (activeChanged) {
          links.forEach((link) => link.classList.remove('is-active'));
          if (nextActive) nextActive.classList.add('is-active');
          activeLink = nextActive;
          if (nextActive) this.keepActiveLinkVisible(nextActive, navInner);
        }

        if (lastSectionEl) {
          const lastRect = lastSectionEl.getBoundingClientRect();
          const lastBottomAbs = lastRect.bottom + window.pageYOffset;
          const shouldHideNav = window.pageYOffset + viewportOffset >= lastBottomAbs;
          stickyNav.classList.toggle('is-nav-hidden', shouldHideNav);
        } else {
          stickyNav.classList.remove('is-nav-hidden');
        }
      };

      const scheduleActiveLinkUpdate = () => {
        if (navUpdateFrame) return;
        navUpdateFrame = window.requestAnimationFrame(updateActiveLink);
      };
      signal.addEventListener(
        'abort',
        () => {
          if (navUpdateFrame) window.cancelAnimationFrame(navUpdateFrame);
          navUpdateFrame = 0;
          stickyNav.classList.remove('is-nav-fixed');
          navPlaceholder.remove();
        },
        { once: true }
      );
      window.addEventListener('scroll', scheduleActiveLinkUpdate, { passive: true, signal });
      window.addEventListener('resize', scheduleActiveLinkUpdate, { passive: true, signal });
      updateActiveLink();
    }

    killStickyNav() {
      if (this._ugMarathonNavAbort) {
        this._ugMarathonNavAbort.abort();
        this._ugMarathonNavAbort = null;
      }

      const stickyNav = this.querySelector('[data-ug-marathon-nav]');
      if (stickyNav) delete stickyNav.dataset.ugMarathonNavBound;
      this.querySelectorAll('.ug-marathon-nav__link').forEach((link) => {
        delete link.dataset.ugMarathonNavLinkBound;
      });
    }

    resolveGiveawayFormId(form) {
      if (!form) return '';

      const direct = String(form.getAttribute('data-id') || form.getAttribute('data-ug-marathon-form-api-id') || '').trim();
      if (direct) return direct;

      const giveaway = form.closest('[data-ug-marathon-giveaway]');
      if (giveaway) {
        const fromGiveaway = String(giveaway.getAttribute('data-ug-marathon-form-api-id') || '').trim();
        if (fromGiveaway) return fromGiveaway;
      }

      const fromRoot = String(this.getAttribute('data-ug-marathon-form-api-id') || '').trim();
      return fromRoot;
    }

    parseTimelineDate(str) {
      if (!str) return null;
      const parts = String(str).trim().split('-');
      if (parts.length !== 3) return null;
      const y = parseInt(parts[0], 10);
      const m = parseInt(parts[1], 10) - 1;
      const d = parseInt(parts[2], 10);
      if (Number.isNaN(y) || Number.isNaN(m) || Number.isNaN(d)) return null;
      const date = new Date(y, m, d);
      date.setHours(0, 0, 0, 0);
      return date;
    }

    syncGiveawayTimelineHeight(timeline) {
      if (!timeline) return;

      const labels = Array.from(timeline.querySelectorAll('.ug-marathon-giveaway__timeline-label'));
      timeline.style.removeProperty('--ug-marathon-timeline-label-height');
      if (!labels.length || !ugMarathonIsMobile()) return;

      let maxHeight = 0;
      labels.forEach((label) => {
        maxHeight = Math.max(maxHeight, label.scrollHeight || 0);
      });

      if (maxHeight > 0) {
        timeline.style.setProperty('--ug-marathon-timeline-label-height', Math.ceil(maxHeight) + 'px');
      }
    }

    initGiveawayTimeline(section) {
      const timeline = section.querySelector('[data-ug-marathon-giveaway-timeline]');
      if (!timeline) return;

      const items = Array.from(timeline.querySelectorAll('[data-ug-marathon-timeline-item]'));
      if (!items.length) return;

      this.syncGiveawayTimelineHeight(timeline);

      const dates = items.map((item) => this.parseTimelineDate(item.getAttribute('data-timeline-date')));
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      let activeIndex = -1;
      dates.forEach((date, index) => {
        if (!date) return;
        if (date.getTime() <= today.getTime()) activeIndex = index;
      });

      items.forEach((item, index) => {
        item.classList.toggle('is-active', activeIndex >= 0 && index <= activeIndex);
      });

      if (!timeline._ugMarathonTimelineHeightResize) {
        timeline._ugMarathonTimelineHeightResize = ugMarathonDebounce(() => {
          ugMarathonSyncMobileState();
          this.syncGiveawayTimelineHeight(timeline);
        }, 150);
        window.addEventListener('resize', timeline._ugMarathonTimelineHeightResize, { passive: true });
      }
    }

    giveawayEmailOk(value) {
      const s = String(value || '').trim();
      if (!s) return false;
      return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(s);
    }

    killGiveawayProductScroll() {
      if (this._ugMarathonGiveawayProductResize) {
        window.removeEventListener('resize', this._ugMarathonGiveawayProductResize);
        this._ugMarathonGiveawayProductResize = null;
      }

      (this._ugMarathonGiveawayProductAnimations || []).forEach((entry) => {
        if (entry.tween) {
          if (entry.tween.scrollTrigger) entry.tween.scrollTrigger.kill();
          entry.tween.kill();
        }
        if (entry.observer) entry.observer.disconnect();
        if (entry.onScroll) {
          window.removeEventListener('scroll', entry.onScroll);
          window.removeEventListener('resize', entry.onScroll);
        }
        if (entry.card) entry.card.classList.remove('is-product-settled');
        if (entry.product && window.gsap) {
          entry.product.classList.remove('is-gsap-ready');
          entry.product._ugMarathonGaPlayed = false;
          window.gsap.set(entry.product, {
            clearProps: 'transform,left,right,bottom,top,x,y,xPercent,yPercent,width',
          });
          if (entry.target && entry.target !== entry.product) {
            window.gsap.set(entry.target, {
              clearProps: 'transform,left,right,bottom,top,x,y,xPercent,yPercent,width',
            });
          }
          if (entry.img) {
            window.gsap.set(entry.img, { clearProps: 'transform,rotation' });
          }
        }
      });
      this._ugMarathonGiveawayProductAnimations = null;
      this._ugMarathonGiveawayProductWasMobile = null;
    }

    getGiveawayProductRise(product) {
      const isMobile = ugMarathonIsMobile();
      const media = product.querySelector(
        isMobile
          ? '.ug-marathon-giveaway__product-media--mb'
          : '.ug-marathon-giveaway__product-media--pc'
      );
      const img = media ? media.querySelector('.ug-marathon-giveaway__product-img') : null;

      if (img) {
        const height = img.getBoundingClientRect().height || img.offsetHeight;
        if (height > 0) return height;
      }

      return isMobile ? 150 : 382;
    }

    bindOneGiveawayProductScroll(product) {
      const gsap = window.gsap;
      const card = product.closest('.ug-marathon-giveaway__card');
      if (!card || !gsap) return null;

      const isMobile = ugMarathonIsMobile();
      const media = product.querySelector(
        isMobile
          ? '.ug-marathon-giveaway__product-media--mb'
          : '.ug-marathon-giveaway__product-media--pc'
      );
      const rise = this.getGiveawayProductRise(product);

      product.classList.add('is-gsap-ready');
      card.classList.remove('is-product-settled');

      let target = product;
      let img = null;
      const gsapClear = 'transform,left,right,bottom,top,x,y,xPercent,yPercent,width';

      if (isMobile && media) {
        gsap.set(product, { clearProps: gsapClear });
        gsap.set(media, { clearProps: gsapClear });
        target = media;
        img = media.querySelector('.ug-marathon-giveaway__product-img');
        gsap.set(target, { y: rise, x: 0, force3D: true });
        if (img) gsap.set(img, { rotation: 0, transformOrigin: '50% 70%' });
      } else {
        gsap.set(target, {
          y: rise,
          force3D: true,
        });
      }

      const markSettled = () => {
        card.classList.add('is-product-settled');
      };

      if (ugMarathonPrefersReducedMotion()) {
        gsap.set(target, { y: 0, x: 0 });
        if (img) gsap.set(img, { rotation: 0 });
        markSettled();
        return { tween: null, observer: null, onScroll: null, product, target, card, img };
      }

      // MB: scrub — slide up first, then rotate; scroll back reverses rotation.
      if (isMobile && media) {
        ugMarathonEnsureGsapRegistered();
        const tl = gsap.timeline({
          defaults: { ease: 'none' },
          scrollTrigger: {
            trigger: card,
            start: 'top 88%',
            end: 'top 28%',
            scrub: 0.55,
            invalidateOnRefresh: true,
            onUpdate: (self) => {
              if (self.progress >= 0.5) markSettled();
            },
          },
        });

        tl.to(target, { y: 0, x: 0, duration: 1 }, 0);
        if (img) {
          tl.to(img, { rotation: 8, duration: 1 }, 1);
        }

        return { tween: tl, observer: null, onScroll: null, product, target, card, img };
      }

      // PC: one-shot rise; hover handles rotate/scale.
      const entry = {
        tween: null,
        observer: null,
        onScroll: null,
        product,
        target,
        card,
        img,
      };

      const playOnce = () => {
        if (product._ugMarathonGaPlayed) return;
        product._ugMarathonGaPlayed = true;
        if (entry.observer) {
          entry.observer.disconnect();
          entry.observer = null;
        }
        if (entry.onScroll) {
          window.removeEventListener('scroll', entry.onScroll);
          window.removeEventListener('resize', entry.onScroll);
          entry.onScroll = null;
        }
        entry.tween = gsap.to(target, {
          y: 0,
          x: 0,
          duration: UG_MARATHON_GA_PRODUCT_DUR,
          ease: 'power2.out',
          overwrite: 'auto',
          onComplete: markSettled,
        });
      };

      const checkAndPlay = () => {
        if (product._ugMarathonGaPlayed) return;
        const vh = window.innerHeight || document.documentElement.clientHeight || 0;
        const rect = card.getBoundingClientRect();
        if (rect.top <= vh * 0.88 && rect.bottom >= 0) {
          playOnce();
        }
      };

      if (typeof IntersectionObserver !== 'undefined') {
        entry.observer = new IntersectionObserver(
          (entries) => {
            if (!entries.some((ioEntry) => ioEntry.isIntersecting)) return;
            playOnce();
          },
          { root: null, rootMargin: '0px 0px -12% 0px', threshold: 0 }
        );
        entry.observer.observe(card);
      }

      entry.onScroll = ugMarathonRafThrottle(checkAndPlay);
      window.addEventListener('scroll', entry.onScroll, { passive: true });
      window.addEventListener('resize', entry.onScroll, { passive: true });
      requestAnimationFrame(checkAndPlay);

      return entry;
    }

    bindGiveawayProductScroll() {
      if (!this.querySelector('[data-ug-marathon-giveaway-product]')) return;

      const gsap = window.gsap;
      // Match KV: without GSAP, still show product in final settled state (CSS hides until ready).
      if (!gsap) {
        this.querySelectorAll('[data-ug-marathon-giveaway-product]').forEach((product) => {
          product.classList.add('is-gsap-ready');
          product.style.removeProperty('transform');
          const card = product.closest('.ug-marathon-giveaway__card');
          if (card) card.classList.add('is-product-settled');
          const mediaMb = product.querySelector('.ug-marathon-giveaway__product-media--mb');
          if (mediaMb) mediaMb.style.transform = 'translate3d(0, 0, 0)';
        });
        return;
      }

      this.killGiveawayProductScroll();
      ugMarathonEnsureGsapRegistered();

      const animations = [];

      this.querySelectorAll('[data-ug-marathon-giveaway-product]').forEach((product) => {
        const entry = this.bindOneGiveawayProductScroll(product);
        if (entry) animations.push(entry);
      });

      this._ugMarathonGiveawayProductAnimations = animations;
      this._ugMarathonGiveawayProductWasMobile = ugMarathonIsMobile();
      this._ugMarathonGiveawayProductResize = ugMarathonDebounce(() => {
        ugMarathonSyncMobileState();
        const nowMobile = ugMarathonIsMobile();
        if (nowMobile !== this._ugMarathonGiveawayProductWasMobile) {
          this._ugMarathonGiveawayProductWasMobile = nowMobile;
          this.bindGiveawayProductScroll();
        }
      }, 150);
      window.addEventListener('resize', this._ugMarathonGiveawayProductResize, { passive: true });
    }

    deferGiveawayProductScroll() {
      if (!this.querySelector('[data-ug-marathon-giveaway-product]')) return;

      const run = () => {
        this.bindGiveawayProductScroll();
      };

      const section = this.querySelector('[data-ug-marathon-giveaway]');
      if (!section || typeof IntersectionObserver === 'undefined') {
        run();
        return;
      }

      if (this._ugMarathonGiveawayIo) {
        this._ugMarathonGiveawayIo.disconnect();
      }

      const io = new IntersectionObserver(
        (entries, observer) => {
          if (!entries.some((entry) => entry.isIntersecting)) return;
          observer.disconnect();
          this._ugMarathonGiveawayIo = null;
          run();
        },
        { root: null, rootMargin: '160px 0px', threshold: 0 }
      );

      this._ugMarathonGiveawayIo = io;
      io.observe(section);
    }

    bindGiveawayForms() {
      const endpoint = 'https://ecf.cirkleinc.com/api/form-submit';
      const root = this;

      this.querySelectorAll('.ug-marathon-giveaway-form').forEach(function (form) {
        if (form._ugMarathonGiveawayFormBound) return;
        form._ugMarathonGiveawayFormBound = true;

        const emailInput = form.querySelector('.ug-marathon-giveaway__input-email');
        const checkboxInput = form.querySelector('.ug-marathon-giveaway__checkbox');
        const submitBtn = form.querySelector('.ug-marathon-giveaway__submit');
        const successMsg = form.querySelector('.ug-marathon-giveaway__msg-success');
        const errorMsg = form.querySelector('.ug-marathon-giveaway__msg-error');

        function clearMessages() {
          if (successMsg) {
            successMsg.textContent = '';
            successMsg.classList.remove('is-visible');
          }
          if (errorMsg) {
            errorMsg.textContent = '';
            errorMsg.classList.remove('is-visible');
          }
        }

        function showSuccess(text) {
          if (errorMsg) {
            errorMsg.textContent = '';
            errorMsg.classList.remove('is-visible');
          }
          if (successMsg) {
            successMsg.textContent = text;
            successMsg.classList.add('is-visible');
          }
        }

        function showError(text) {
          if (successMsg) {
            successMsg.textContent = '';
            successMsg.classList.remove('is-visible');
          }
          if (errorMsg) {
            errorMsg.textContent = text;
            errorMsg.classList.add('is-visible');
          }
        }

        function onSubmit(e) {
          e.preventDefault();

          if (submitBtn && submitBtn.classList.contains('loading')) return;

          clearMessages();

          const formId = root.resolveGiveawayFormId(form);
          const msgOk = form.getAttribute('data-success') || 'Thank you for subscribing!';
          const errEmail = form.getAttribute('data-email-error') || 'Please enter your email address.';
          const errEmailFmt = form.getAttribute('data-email-invalid') || 'Please enter a valid email address.';
          const errTerms =
            form.getAttribute('data-terms-error') || 'Please agree to the Terms of Use and Privacy Policy.';
          const submittingLabel = form.getAttribute('data-submitting-label') || 'Submitting...';
          const originalBtnText = submitBtn ? submitBtn.textContent : '';
          const raw = emailInput && emailInput.value ? String(emailInput.value).trim() : '';

          if (!raw) {
            showError(errEmail);
            return;
          }
          if (!root.giveawayEmailOk(raw)) {
            showError(errEmailFmt);
            return;
          }
          if (checkboxInput && !checkboxInput.checked) {
            showError(errTerms);
            return;
          }
          if (!formId) {
            showError('Form is not configured (missing API id).');
            return;
          }

          if (submitBtn) {
            submitBtn.classList.add('loading');
            submitBtn.disabled = true;
            submitBtn.textContent = submittingLabel;
          }

          fetch(endpoint, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              email: raw,
              accepts_marketing: checkboxInput && checkboxInput.checked ? 'on' : 'off',
              form_id: formId,
            }),
          })
            .then(function (response) {
              return response.json();
            })
            .then(function (data) {
              if (submitBtn) {
                submitBtn.classList.remove('loading');
                submitBtn.disabled = false;
                submitBtn.textContent = originalBtnText;
              }

              const formOk =
                data &&
                (data.status === 'success' ||
                  data.success === true ||
                  String(data.status || '').toLowerCase() === 'success');

              if (formOk) {
                if (emailInput) emailInput.value = '';
                if (checkboxInput) checkboxInput.checked = false;
                if (root.querySelector('[data-ug-marathon-success]')) {
                  root.openSuccessModal();
                } else {
                  showSuccess(msgOk || data.message || 'Thank you for subscribing!');
                }

                if (window.ugGa4Track) {
                  window.ugGa4Track.subscriptionEvent({
                    isSuccess: true,
                    position: 'ug-marathon-giveaway',
                  });
                }
              } else {
                const errorText =
                  (data && data.errors && data.errors.email && data.errors.email[0]) ||
                  (data && data.message) ||
                  'Something went wrong. Please try again.';
                showError(errorText);

                if (window.ugGa4Track) {
                  window.ugGa4Track.subscriptionEvent({
                    isSuccess: false,
                    position: 'ug-marathon-giveaway',
                  });
                }
              }
            })
            .catch(function (error) {
              if (submitBtn) {
                submitBtn.classList.remove('loading');
                submitBtn.disabled = false;
                submitBtn.textContent = originalBtnText;
              }
              showError('Network error. Please try again later.');
              console.error('Subscription Error:', error);

              if (window.ugGa4Track) {
                window.ugGa4Track.subscriptionEvent({
                  isSuccess: false,
                  position: 'ug-marathon-giveaway',
                });
              }
            });
        }

        form._ugMarathonGiveawayClearMessages = clearMessages;
        form._ugMarathonGiveawayOnSubmit = onSubmit;
        form._ugMarathonGiveawayEmailInput = emailInput || null;
        form._ugMarathonGiveawayCheckboxInput = checkboxInput || null;

        if (emailInput) emailInput.addEventListener('input', clearMessages);
        if (checkboxInput) checkboxInput.addEventListener('change', clearMessages);
        form.addEventListener('submit', onSubmit);
      });
    }

    bindSuccessModal() {
      const modal = this.querySelector('[data-ug-marathon-success]');
      if (!modal || modal._ugMarathonSuccessBound) return;
      modal._ugMarathonSuccessBound = true;

      const onClick = (event) => {
        if (event.target.closest('[data-ug-marathon-success-close]')) {
          this.closeSuccessModal();
        }
      };
      const onKey = (event) => {
        if (event.key === 'Escape' && modal.classList.contains('is-open')) {
          this.closeSuccessModal();
        }
      };

      modal.addEventListener('click', onClick);
      document.addEventListener('keydown', onKey);
      modal._ugMarathonSuccessOnClick = onClick;
      this._ugMarathonSuccessOnKey = onKey;
    }

    openSuccessModal() {
      const modal = this.querySelector('[data-ug-marathon-success]');
      if (!modal) return;
      modal.hidden = false;
      modal.classList.add('is-open');
      modal.setAttribute('aria-hidden', 'false');
      document.documentElement.classList.add('ug-marathon-success-lock');
      const closeBtn = modal.querySelector('.ug-marathon-success__close');
      if (closeBtn) closeBtn.focus();
    }

    closeSuccessModal() {
      const modal = this.querySelector('[data-ug-marathon-success]');
      if (!modal) return;
      modal.hidden = true;
      modal.classList.remove('is-open');
      modal.setAttribute('aria-hidden', 'true');
      document.documentElement.classList.remove('ug-marathon-success-lock');
    }

    killSuccessModal() {
      this.closeSuccessModal();
      const modal = this.querySelector('[data-ug-marathon-success]');
      if (modal) {
        if (modal._ugMarathonSuccessOnClick) {
          modal.removeEventListener('click', modal._ugMarathonSuccessOnClick);
          modal._ugMarathonSuccessOnClick = null;
        }
        modal._ugMarathonSuccessBound = false;
      }
      if (this._ugMarathonSuccessOnKey) {
        document.removeEventListener('keydown', this._ugMarathonSuccessOnKey);
        this._ugMarathonSuccessOnKey = null;
      }
    }

    bindGiveaway() {
      this.querySelectorAll('[data-ug-marathon-giveaway]').forEach((section) => {
        this.initGiveawayTimeline(section);
      });
      this.bindGiveawayForms();
      this.deferGiveawayProductScroll();
    }

    killGiveaway() {
      this.querySelectorAll('.ug-marathon-giveaway-form').forEach((form) => {
        const clearMessages = form._ugMarathonGiveawayClearMessages;
        const onSubmit = form._ugMarathonGiveawayOnSubmit;
        const emailInput = form._ugMarathonGiveawayEmailInput;
        const checkboxInput = form._ugMarathonGiveawayCheckboxInput;

        if (onSubmit) form.removeEventListener('submit', onSubmit);
        if (emailInput && clearMessages) emailInput.removeEventListener('input', clearMessages);
        if (checkboxInput && clearMessages) checkboxInput.removeEventListener('change', clearMessages);

        form._ugMarathonGiveawayFormBound = false;
        form._ugMarathonGiveawayClearMessages = null;
        form._ugMarathonGiveawayOnSubmit = null;
        form._ugMarathonGiveawayEmailInput = null;
        form._ugMarathonGiveawayCheckboxInput = null;
      });

      this.querySelectorAll('[data-ug-marathon-giveaway-timeline]').forEach((timeline) => {
        timeline.style.removeProperty('--ug-marathon-timeline-label-height');
        if (timeline._ugMarathonTimelineHeightResize) {
          window.removeEventListener('resize', timeline._ugMarathonTimelineHeightResize);
          timeline._ugMarathonTimelineHeightResize = null;
        }
      });

      if (this._ugMarathonGiveawayIo) {
        this._ugMarathonGiveawayIo.disconnect();
        this._ugMarathonGiveawayIo = null;
      }

      this.killGiveawayProductScroll();
    }
  }

  if (!customElements.get('ug-marathon-section')) {
    customElements.define('ug-marathon-section', UgMarathonSection);
  }
})();